import { ActionBar, ActionBarSeparator, AlertModal, Button, Select, showToast, TextBlock } from "@bcc-ui/ui-components";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GRADING_ACTION_KEYS, GradingAction } from "domain/asset-management/assets";
import { AUTH_ASSET_EDIT } from "domain/authority";
import { assetService } from "services/asset-management/AssetService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    selectedCount: number;
    pageAssetCount: number;
    selectedAssetUuids: string[];
    deselectAll: () => void;
    selectAllOnPage: () => void;
    onActionApplied: () => void;
    onApplyingChange: (isApplying: boolean) => void;
}

export const ValueRecoveryActionBar = (props: Props) => {
    const { t } = useTranslation();
    const abortControllerRef = useRef(new AbortController());
    const [isApplying, setIsApplying] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    const canEditAsset = userSessionService.userHasAllAuthorities([AUTH_ASSET_EDIT]);

    const handleSelectAction = (value: string | number) => {
        const action = value as GradingAction;
        setIsApplying(true);
        props.onApplyingChange(true);
        const abortController = abortControllerRef.current;
        assetService
            .bulkSetValueRecoveryAction(action, props.selectedAssetUuids, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    const failedCount = response.failedAssetUuids.length;
                    if (failedCount === 0) {
                        showToast(t("AssetManagement.value.gradingTable.actionSaved"), "success");
                        props.onActionApplied();
                    } else if (failedCount === props.selectedAssetUuids.length) {
                        setSaveFailed(true);
                    } else {
                        showToast(
                            t("AssetManagement.value.gradingTable.actionPartialFailure", { count: failedCount }),
                            "info"
                        );
                        props.onActionApplied();
                    }
                }
            })
            .catch((exception) => {
                if (!abortController.signal.aborted) {
                    logger.error(
                        `Failed to apply value recovery action to ${props.selectedAssetUuids.length} assets.`,
                        exception.message
                    );
                    setSaveFailed(true);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsApplying(false);
                    props.onApplyingChange(false);
                }
            });
    };

    return (
        <>
            <ActionBar data-testid={testIds.workArea.asset.customView.value.bulkActions.itself}>
                <TextBlock
                    disableBottomSpacing
                    testId={testIds.workArea.asset.customView.value.bulkActions.selectedCount}
                >
                    {t("AssetManagement.value.gradingTable.bulkActions.selectedCount", { count: props.selectedCount })}
                </TextBlock>

                <ActionBarSeparator />

                <Button
                    variant="Tertiary"
                    onClick={props.deselectAll}
                    testId={testIds.workArea.asset.customView.value.bulkActions.deselectButton}
                >
                    {t("AssetManagement.value.gradingTable.bulkActions.deselect")}
                </Button>

                <Button
                    variant="Tertiary"
                    onClick={props.selectAllOnPage}
                    testId={testIds.workArea.asset.customView.value.bulkActions.selectAllButton}
                >
                    {t("AssetManagement.value.gradingTable.bulkActions.selectAll", { count: props.pageAssetCount })}
                </Button>

                {canEditAsset && (
                    <>
                        <ActionBarSeparator />

                        <Select
                            name="valueRecoveryBulkAction"
                            placeholder={t("AssetManagement.value.gradingTable.bulkActions.applyActionPlaceholder")}
                            hideLabelSection
                            disabled={isApplying}
                            dropdownPosition="top"
                            testIds={{ input: testIds.workArea.asset.customView.value.bulkActions.actionSelect }}
                            items={GRADING_ACTION_KEYS.map((key) => ({
                                value: key,
                                label: t(`AssetManagement.value.gradingTable.actions.${key}`),
                            }))}
                            onSelection={handleSelectAction}
                        />
                    </>
                )}
            </ActionBar>

            <AlertModal
                variant="Error"
                title={t("AssetManagement.value.gradingTable.actionSaveFailedTitle")}
                isOpen={saveFailed}
                hideModal={() => setSaveFailed(false)}
                buttons={
                    <Button variant="Primary" onClick={() => setSaveFailed(false)}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                {t("AssetManagement.value.gradingTable.actionSaveFailed")}
            </AlertModal>
        </>
    );
};
